import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent } from '../../components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from '../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Plus, Edit, Trash2, Building2, MapPin, Search, ChevronRight, ChevronDown, Star,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import AdminSidebar from '../../components/admin/AdminSidebar';

const EMPTY_COMPANY = {
  legal_name: '',
  trade_name: '',
  cnpj_root: '',
  industry: '',
  size: '',
  city: '',
  state: '',
  main_contact_name: '',
  main_contact_email: '',
  main_contact_phone: '',
  notes: '',
};

const EMPTY_BRANCH = {
  name: '',
  cnpj: '',
  address: '',
  city: '',
  state: '',
  zipcode: '',
  phone: '',
  manager_name: '',
  manager_email: '',
  employee_count: '',
  is_headquarters: false,
  notes: '',
};

const SIZE_LABELS = { small: 'Pequena', medium: 'Média', large: 'Grande' };

export default function CompaniesPage() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [companyDialog, setCompanyDialog] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [companyForm, setCompanyForm] = useState(EMPTY_COMPANY);
  const [deleteCompanyId, setDeleteCompanyId] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [branchDialog, setBranchDialog] = useState(false);
  const [branchCompanyId, setBranchCompanyId] = useState(null);
  const [editingBranch, setEditingBranch] = useState(null);
  const [branchForm, setBranchForm] = useState(EMPTY_BRANCH);
  const [deleteBranchId, setDeleteBranchId] = useState(null);
  const [search, setSearch] = useState('');

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetch = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/companies/`, {
        withCredentials: true,
        params: search.trim() ? { search: search.trim() } : {},
      });
      setCompanies(data);
    } catch (error) {
      toast.error('Erro ao carregar empresas');
    } finally {
      setLoading(false);
    }
  }, [API_URL, search]);

  useEffect(() => {
    const t = setTimeout(fetch, 250);
    return () => clearTimeout(t);
  }, [fetch]);

  const submitCompany = async (e) => {
    e.preventDefault();
    if (!companyForm.legal_name.trim()) {
      toast.error('Razão social é obrigatória');
      return;
    }
    try {
      const payload = { ...companyForm };
      if (!payload.main_contact_email) delete payload.main_contact_email;
      if (!payload.size) delete payload.size;
      if (editingCompany) {
        await axios.put(`${API_URL}/api/companies/${editingCompany.id}`, payload, { withCredentials: true });
        toast.success('Empresa atualizada');
      } else {
        await axios.post(`${API_URL}/api/companies/`, payload, { withCredentials: true });
        toast.success('Empresa criada');
      }
      setCompanyDialog(false);
      resetCompanyForm();
      fetch();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar');
    }
  };

  const submitBranch = async (e) => {
    e.preventDefault();
    if (!branchForm.name.trim()) {
      toast.error('Nome da filial é obrigatório');
      return;
    }
    try {
      const payload = { ...branchForm };
      if (!payload.manager_email) delete payload.manager_email;
      if (payload.employee_count === '' || payload.employee_count === null) {
        delete payload.employee_count;
      } else {
        payload.employee_count = parseInt(payload.employee_count, 10);
      }
      if (editingBranch) {
        await axios.put(`${API_URL}/api/companies/branches/${editingBranch.id}`, payload, { withCredentials: true });
        toast.success('Filial atualizada');
      } else {
        await axios.post(`${API_URL}/api/companies/${branchCompanyId}/branches`, payload, { withCredentials: true });
        toast.success('Filial adicionada');
      }
      setBranchDialog(false);
      resetBranchForm();
      fetch();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar filial');
    }
  };

  const editCompany = (c) => {
    setEditingCompany(c);
    setCompanyForm({
      legal_name: c.legal_name || '',
      trade_name: c.trade_name || '',
      cnpj_root: c.cnpj_root || '',
      industry: c.industry || '',
      size: c.size || '',
      city: c.city || '',
      state: c.state || '',
      main_contact_name: c.main_contact_name || '',
      main_contact_email: c.main_contact_email || '',
      main_contact_phone: c.main_contact_phone || '',
      notes: c.notes || '',
    });
    setCompanyDialog(true);
  };

  const editBranch = (b) => {
    setEditingBranch(b);
    setBranchCompanyId(b.company_id);
    setBranchForm({
      name: b.name || '',
      cnpj: b.cnpj || '',
      address: b.address || '',
      city: b.city || '',
      state: b.state || '',
      zipcode: b.zipcode || '',
      phone: b.phone || '',
      manager_name: b.manager_name || '',
      manager_email: b.manager_email || '',
      employee_count: b.employee_count ?? '',
      is_headquarters: b.is_headquarters || false,
      notes: b.notes || '',
    });
    setBranchDialog(true);
  };

  const openNewBranch = (companyId) => {
    setEditingBranch(null);
    setBranchCompanyId(companyId);
    setBranchForm(EMPTY_BRANCH);
    setBranchDialog(true);
  };

  const deleteCompany = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/companies/${id}`, { withCredentials: true });
      toast.success('Empresa arquivada');
      fetch();
    } catch (error) {
      toast.error('Erro ao remover');
    } finally {
      setDeleteCompanyId(null);
    }
  };

  const deleteBranch = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/companies/branches/${id}`, { withCredentials: true });
      toast.success('Filial removida');
      fetch();
    } catch (error) {
      toast.error('Erro ao remover');
    } finally {
      setDeleteBranchId(null);
    }
  };

  const resetCompanyForm = () => { setEditingCompany(null); setCompanyForm(EMPTY_COMPANY); };
  const resetBranchForm = () => { setEditingBranch(null); setBranchForm(EMPTY_BRANCH); setBranchCompanyId(null); };

  const toggle = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  return (
    <AdminSidebar
      title="Empresas Clientes"
      subtitle="Cadastro de matriz + filiais para diagnóstico de inclusão"
    >
      <Card>
        <CardContent className="p-4 md:p-6">
          <div className="flex flex-wrap gap-3 mb-4">
            <div className="flex-1 min-w-[200px] relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Buscar empresa por nome ou CNPJ..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="companies-search"
              />
            </div>
            <Dialog open={companyDialog} onOpenChange={(o) => { setCompanyDialog(o); if (!o) resetCompanyForm(); }}>
              <DialogTrigger asChild>
                <Button data-testid="new-company-button">
                  <Plus className="mr-2 h-4 w-4" /> Nova Empresa
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingCompany ? 'Editar Empresa' : 'Nova Empresa'}</DialogTitle>
                  <DialogDescription>
                    Cadastro da matriz. Filiais podem ser adicionadas depois.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={submitCompany} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2 md:col-span-2">
                      <Label htmlFor="legal_name">Razão Social *</Label>
                      <Input id="legal_name" value={companyForm.legal_name} onChange={(e) => setCompanyForm({ ...companyForm, legal_name: e.target.value })} required data-testid="company-legal-name-input" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="trade_name">Nome Fantasia</Label>
                      <Input id="trade_name" value={companyForm.trade_name} onChange={(e) => setCompanyForm({ ...companyForm, trade_name: e.target.value })} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="cnpj_root">CNPJ raiz</Label>
                      <Input id="cnpj_root" placeholder="12345678" value={companyForm.cnpj_root} onChange={(e) => setCompanyForm({ ...companyForm, cnpj_root: e.target.value })} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="industry">Setor</Label>
                      <Input id="industry" value={companyForm.industry} onChange={(e) => setCompanyForm({ ...companyForm, industry: e.target.value })} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="size">Porte</Label>
                      <select id="size" value={companyForm.size} onChange={(e) => setCompanyForm({ ...companyForm, size: e.target.value })} className="w-full h-10 px-3 rounded-md border border-input bg-white text-sm">
                        <option value="">Selecione...</option>
                        <option value="small">Pequena</option>
                        <option value="medium">Média</option>
                        <option value="large">Grande</option>
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="city">Cidade</Label>
                      <Input id="city" value={companyForm.city} onChange={(e) => setCompanyForm({ ...companyForm, city: e.target.value })} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="state">UF</Label>
                      <Input id="state" maxLength={2} value={companyForm.state} onChange={(e) => setCompanyForm({ ...companyForm, state: e.target.value.toUpperCase() })} />
                    </div>
                    <div className="space-y-2 md:col-span-2 border-t pt-3">
                      <Label className="text-sm font-semibold text-gray-800">Contato principal</Label>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="main_contact_name">Nome</Label>
                      <Input id="main_contact_name" value={companyForm.main_contact_name} onChange={(e) => setCompanyForm({ ...companyForm, main_contact_name: e.target.value })} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="main_contact_email">E-mail</Label>
                      <Input id="main_contact_email" type="email" value={companyForm.main_contact_email} onChange={(e) => setCompanyForm({ ...companyForm, main_contact_email: e.target.value })} />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <Label htmlFor="main_contact_phone">Telefone</Label>
                      <Input id="main_contact_phone" value={companyForm.main_contact_phone} onChange={(e) => setCompanyForm({ ...companyForm, main_contact_phone: e.target.value })} />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <Label htmlFor="notes">Observações</Label>
                      <Textarea id="notes" rows={2} value={companyForm.notes} onChange={(e) => setCompanyForm({ ...companyForm, notes: e.target.value })} />
                    </div>
                  </div>
                  <div className="flex justify-end gap-2 pt-2">
                    <Button type="button" variant="outline" onClick={() => { setCompanyDialog(false); resetCompanyForm(); }}>Cancelar</Button>
                    <Button type="submit" data-testid="company-save-button">{editingCompany ? 'Atualizar' : 'Criar'}</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
            </div>
          ) : companies.length === 0 ? (
            <div className="text-center py-16 text-gray-500">
              <Building2 size={40} className="mx-auto text-gray-300 mb-3" />
              Nenhuma empresa cadastrada. Cadastre a primeira ou converta um lead.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10"></TableHead>
                  <TableHead>Empresa</TableHead>
                  <TableHead>Setor / Porte</TableHead>
                  <TableHead>Localização</TableHead>
                  <TableHead className="text-center">Filiais</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {companies.map((c) => (
                  <React.Fragment key={c.id}>
                    <TableRow data-testid={`company-row-${c.id}`}>
                      <TableCell>
                        <button onClick={() => toggle(c.id)} className="text-gray-500 hover:text-gray-800" aria-label={expanded[c.id] ? 'Recolher' : 'Expandir'}>
                          {expanded[c.id] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                        </button>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">{c.trade_name || c.legal_name}</div>
                        <div className="text-xs text-gray-500">{c.legal_name}</div>
                        {c.cnpj_root && <div className="text-xs text-gray-400 mt-0.5">CNPJ: {c.cnpj_root}</div>}
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">{c.industry || '-'}</div>
                        {c.size && <div className="text-xs text-gray-500">{SIZE_LABELS[c.size]}</div>}
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">{c.city}{c.state ? `/${c.state}` : ''}</div>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-teal-50 text-teal-700 text-xs font-semibold">
                          <Building2 size={10} />{c.branches_count}
                        </span>
                      </TableCell>
                      <TableCell className="text-right space-x-1 whitespace-nowrap">
                        <Button variant="ghost" size="sm" onClick={() => openNewBranch(c.id)} title="Adicionar filial" data-testid={`add-branch-${c.id}`}>
                          <Plus className="h-4 w-4 text-teal-600" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => editCompany(c)} data-testid={`edit-company-${c.id}`}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setDeleteCompanyId(c.id)} data-testid={`delete-company-${c.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                    {expanded[c.id] && (
                      <TableRow className="bg-gray-50/60">
                        <TableCell></TableCell>
                        <TableCell colSpan={5} className="py-3">
                          {c.branches.length === 0 ? (
                            <div className="text-sm text-gray-500 italic">Nenhuma filial. Use o botão + para adicionar.</div>
                          ) : (
                            <div className="space-y-2">
                              {c.branches.map((b) => (
                                <div key={b.id} className="flex items-center justify-between bg-white border border-gray-200 rounded-md p-3" data-testid={`branch-row-${b.id}`}>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                      {b.is_headquarters && (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-bold uppercase">
                                          <Star size={10} /> Matriz
                                        </span>
                                      )}
                                      <span className="font-medium text-gray-900">{b.name}</span>
                                      {b.cnpj && <span className="text-xs text-gray-400">CNPJ {b.cnpj}</span>}
                                    </div>
                                    <div className="text-xs text-gray-500 mt-1 flex flex-wrap gap-3">
                                      {(b.city || b.state) && <span className="inline-flex items-center gap-1"><MapPin size={10} />{b.city}{b.state ? `/${b.state}` : ''}</span>}
                                      {b.manager_name && <span>Gestor: {b.manager_name}</span>}
                                      {b.employee_count != null && <span>{b.employee_count} funcionários</span>}
                                    </div>
                                  </div>
                                  <div className="flex gap-1 shrink-0">
                                    <Button variant="ghost" size="sm" onClick={() => editBranch(b)} data-testid={`edit-branch-${b.id}`}>
                                      <Edit className="h-4 w-4" />
                                    </Button>
                                    <Button variant="ghost" size="sm" onClick={() => setDeleteBranchId(b.id)} data-testid={`delete-branch-${b.id}`}>
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Branch dialog */}
      <Dialog open={branchDialog} onOpenChange={(o) => { setBranchDialog(o); if (!o) resetBranchForm(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingBranch ? 'Editar Filial' : 'Nova Filial'}</DialogTitle>
            <DialogDescription>Uma empresa pode ter várias filiais (uma delas marcada como matriz).</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitBranch} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="branch_name">Nome da unidade *</Label>
                <Input id="branch_name" value={branchForm.name} onChange={(e) => setBranchForm({ ...branchForm, name: e.target.value })} required data-testid="branch-name-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="branch_cnpj">CNPJ</Label>
                <Input id="branch_cnpj" value={branchForm.cnpj} onChange={(e) => setBranchForm({ ...branchForm, cnpj: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="branch_employee_count">Nº de funcionários</Label>
                <Input id="branch_employee_count" type="number" min="0" value={branchForm.employee_count} onChange={(e) => setBranchForm({ ...branchForm, employee_count: e.target.value })} />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="branch_address">Endereço</Label>
                <Input id="branch_address" value={branchForm.address} onChange={(e) => setBranchForm({ ...branchForm, address: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="branch_city">Cidade</Label>
                <Input id="branch_city" value={branchForm.city} onChange={(e) => setBranchForm({ ...branchForm, city: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="branch_state">UF</Label>
                <Input id="branch_state" maxLength={2} value={branchForm.state} onChange={(e) => setBranchForm({ ...branchForm, state: e.target.value.toUpperCase() })} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="branch_zipcode">CEP</Label>
                <Input id="branch_zipcode" value={branchForm.zipcode} onChange={(e) => setBranchForm({ ...branchForm, zipcode: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="branch_phone">Telefone</Label>
                <Input id="branch_phone" value={branchForm.phone} onChange={(e) => setBranchForm({ ...branchForm, phone: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="branch_manager_name">Nome do Gestor</Label>
                <Input id="branch_manager_name" value={branchForm.manager_name} onChange={(e) => setBranchForm({ ...branchForm, manager_name: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="branch_manager_email">E-mail do Gestor</Label>
                <Input id="branch_manager_email" type="email" value={branchForm.manager_email} onChange={(e) => setBranchForm({ ...branchForm, manager_email: e.target.value })} />
              </div>
              <div className="flex items-center gap-2 md:col-span-2 pt-2">
                <input
                  type="checkbox"
                  id="is_hq"
                  checked={branchForm.is_headquarters}
                  onChange={(e) => setBranchForm({ ...branchForm, is_headquarters: e.target.checked })}
                  className="w-4 h-4 accent-teal-600"
                  data-testid="branch-hq-checkbox"
                />
                <Label htmlFor="is_hq" className="cursor-pointer text-sm">
                  Esta é a matriz (só pode haver uma por empresa)
                </Label>
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="branch_notes">Observações</Label>
                <Textarea id="branch_notes" rows={2} value={branchForm.notes} onChange={(e) => setBranchForm({ ...branchForm, notes: e.target.value })} />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => { setBranchDialog(false); resetBranchForm(); }}>Cancelar</Button>
              <Button type="submit" data-testid="branch-save-button">{editingBranch ? 'Atualizar' : 'Criar'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirmations */}
      <AlertDialog open={!!deleteCompanyId} onOpenChange={() => setDeleteCompanyId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Arquivar empresa</AlertDialogTitle>
            <AlertDialogDescription>A empresa e suas filiais serão ocultadas. Você pode reativar depois.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => deleteCompany(deleteCompanyId)}>Confirmar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteBranchId} onOpenChange={() => setDeleteBranchId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover filial</AlertDialogTitle>
            <AlertDialogDescription>Esta ação é permanente.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => deleteBranch(deleteBranchId)}>Confirmar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminSidebar>
  );
}
